import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../../supabaseClient';
import { authService } from '../../../../authService';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
  Snackbar,
  Autocomplete,
  Card,
  CardContent,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Fade,
  Menu,
  MenuItem,
  Grid,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Edit as EditIcon,
  PostAdd as PostAddIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  ArrowForward,
  MoveToInbox,
  History as HistoryIcon,
  ExpandMore,
  ExpandLess,
  FactCheck
} from '@mui/icons-material';
import { Collapse,
  TableSortLabel
} from '@mui/material';

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DocumentTracking = () => {
  const theme = useTheme();
  // State
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('ADD_CAFOA'); // ADD_CAFOA, ADD_VOUCHER, ADD_CHECK, EDIT_DETAILS
  const [selectedRow, setSelectedRow] = useState(null);
  const [isBulkAction, setIsBulkAction] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [payeeSuggestions, setPayeeSuggestions] = useState([]);
  const [particularsSuggestions, setParticularsSuggestions] = useState([]);
  const [remarksSuggestions, setRemarksSuggestions] = useState([]);

  // Delete Dialog State
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  // Selection & Actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [voucherSelectionIds, setVoucherSelectionIds] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Refs
  const checkNumberInputRef = useRef(null);

  // Focus effect for ADD_CANCELLED_CHECK
  useEffect(() => {
    if (openDialog && dialogType === 'ADD_CANCELLED_CHECK') {
      // Small timeout to ensure dialog is fully rendered
      const timer = setTimeout(() => {
        if (checkNumberInputRef.current) {
          checkNumberInputRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [openDialog, dialogType]);
  
  // Dialog States for Transfer/Receive
  const [openTransfer, setOpenTransfer] = useState(false);
  const [openReceive, setOpenReceive] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Form Data
  const [transferData, setTransferData] = useState({
    department: '',
    transferred_by: '',
    transferred_date: getLocalDateString(),
    reason: ''
  });

  const [receiveData, setReceiveData] = useState({
    received_by: '',
    received_date: getLocalDateString()
  });

  // History State
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Sorting State
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('updated_at');

  // Fetch Data
  const fetchData = async () => {
    setLoading(true);
    try {
      const batchSize = 1000;
      let from = 0;
      let all = [];

      while (true) {
        const { data: chunk, error } = await supabase
          .from('elogbook_transactions')
          .select('*')
          .order('updated_at', { ascending: false, nullsFirst: false })
          .range(from, from + batchSize - 1);

        if (error) throw error;

        const rows = chunk || [];
        all = all.concat(rows);

        if (rows.length < batchSize) break;
        from += batchSize;
      }

      setData(all);
    } catch (error) {
      console.error('Error fetching document tracking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      // Fetch Payees
      const { data: payeeData, error: payeeError } = await supabase
        .from('elogbook_transactions')
        .select('payee_name')
        .not('payee_name', 'is', null);

      if (payeeError) throw payeeError;
      
      const uniquePayees = [...new Set(payeeData.map(item => item.payee_name))].sort();
      setPayeeSuggestions(uniquePayees);

      // Fetch Particulars
      const { data: partData, error: partError } = await supabase
        .from('elogbook_transactions')
        .select('voucher_particulars')
        .not('voucher_particulars', 'is', null);

      if (partError) throw partError;

      const uniqueParticulars = [...new Set(partData.map(item => item.voucher_particulars))].sort();
      setParticularsSuggestions(uniqueParticulars);

      // Fetch Remarks from Cancelled Checks
      const { data: remarksData, error: remarksError } = await supabase
        .from('elogbook_transactions')
        .select('remarks')
        .eq('status', 'cancelled')
        .not('remarks', 'is', null);

      if (remarksError) throw remarksError;

      // Extract unique reasons from remarks JSON structure
      const reasons = new Set();
      remarksData.forEach(item => {
        let remarksArray = [];
        if (Array.isArray(item.remarks)) {
            remarksArray = item.remarks;
        } else if (typeof item.remarks === 'string') {
            try {
                const parsed = JSON.parse(item.remarks);
                if (Array.isArray(parsed)) remarksArray = parsed;
            } catch (e) {
                // Ignore parsing errors
            }
        }

        remarksArray.forEach(r => {
            if (r.content && r.content.includes('Cancelled Check') && r.reason) {
                reasons.add(r.reason);
            }
        });
      });
      setRemarksSuggestions([...reasons].sort());

    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('department')
        .not('department', 'is', null);

      if (error) throw error;
      
      const uniqueDepts = [...new Set(data.map(u => u.department))].sort();
      setDepartments(uniqueDepts);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments(['Mayor\'s Office', 'Budget', 'Accounting', 'Treasury', 'Engineering', 'MPDO', 'HR', 'GSO']);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSuggestions();
    fetchDepartments();
    const user = authService.getUserSession();
    if (user) {
        setTransferData(prev => ({ ...prev, transferred_by: `${user.firstname} ${user.lastname}` }));
        setReceiveData(prev => ({ ...prev, received_by: `${user.firstname} ${user.lastname}` }));
    }
  }, []);

  // Dialog Handlers
  const handleOpenVoucherSelection = () => {
    setVoucherSelectionIds([]);
    setDialogType('SELECT_VOUCHER_CAFOAS');
    setOpenDialog(true);
  };

  const handleOpenDialog = (type, row = null) => {
    setDialogType(type);
    setSelectedRow(row);
    setIsBulkAction(false);
    setHistoryExpanded(false);
    
    if (type === 'ADD_CAFOA') {
      setFormData({
        cafoa_number: '',
        amount: '',
        payee_name: ''
      });
    } else if (type === 'ADD_VOUCHER') {
      setFormData({
        dv_number: '',
        voucher_type: 'general',
        voucher_payee: row?.payee_name || '',
        voucher_amount: row?.cafoa_amount || '',
        voucher_particulars: ''
      });
    } else if (type === 'ADD_CHECK') {
      setFormData({
        check_number: '',
        check_date: getLocalDateString(),
        check_amount: row?.voucher_amount || row?.cafoa_amount || '',
        check_remarks: ''
      });
    } else if (type === 'ADD_CANCELLED_CHECK') {
      setFormData({
        check_number: '',
        check_date: getLocalDateString(),
        check_amount: '',
        payee_name: 'CANCELLED CHECK',
        remarks: '',
        voucher_type: 'general'
      });
    } else if (type === 'EDIT_DETAILS') {
      setFormData({
        // CAFOA
        cafoa_number: row?.cafoa_number || '',
        payee_name: row?.payee_name || '',
        cafoa_amount: row?.cafoa_amount || '',
        // Voucher
        dv_number: row?.dv_number || '',
        voucher_type: row?.voucher_type || 'general',
        voucher_payee: row?.voucher_payee || '',
        voucher_amount: row?.voucher_amount || '',
        voucher_particulars: row?.voucher_particulars || '',
        // Check
        check_number: row?.check_number || '',
        check_date: row?.check_date ? row.check_date.split('T')[0] : '',
        check_amount: row?.check_amount || '',
        check_remarks: row?.check_remarks || ''
      });
    } else if (type === 'UPDATE_STATUS') {
      setFormData({
        status: row?.status || 'pending'
      });
    } else if (type === 'CLAIM_CHECK') {
      setFormData({
        claimed_by: row?.voucher_payee || row?.payee_name || '',
        claimed_date: getLocalDateString()
      });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
    setIsBulkAction(false);
  };

  const handleBulkAddVoucher = (idsOverride = null) => {
    const idsToUse = Array.isArray(idsOverride) ? idsOverride : selectedIds;
    const selectedRows = data.filter(row => idsToUse.includes(row.id));
    
    // Validation
    const invalidRows = selectedRows.filter(row => row.dv_number);
    if (invalidRows.length > 0) {
      setSnackbar({
        open: true,
        message: `Cannot create voucher: ${invalidRows.length} selected item(s) already have a voucher.`,
        severity: 'error'
      });
      return;
    }

    if (selectedRows.length === 0) return;

    // Calculate total amount
    const totalAmount = selectedRows.reduce((sum, row) => sum + (parseFloat(row.cafoa_amount) || 0), 0);
    
    // Get Payee (use first one as default)
    const defaultPayee = selectedRows[0].payee_name;
    
    setFormData({
      voucher_amount: totalAmount,
      voucher_payee: defaultPayee,
      voucher_type: 'general'
    });
    
    setDialogType('ADD_VOUCHER');
    setIsBulkAction(true);
    setOpenDialog(true);
  };

  const handleTransfer = async () => {
    if (!transferData.department) {
      setSnackbar({ open: true, message: 'Please select a department', severity: 'error' });
      return;
    }

    setActionLoading(true);
    try {
      const user = authService.getUserSession();
      for (const id of selectedIds) {
        const transaction = data.find(t => t.id === id);
        if (!transaction) continue;

        let currentRemarks = [];
        if (transaction.remarks) {
            if (Array.isArray(transaction.remarks)) {
                currentRemarks = [...transaction.remarks];
            } else if (typeof transaction.remarks === 'string') {
                try {
                    const parsed = JSON.parse(transaction.remarks);
                    if (Array.isArray(parsed)) {
                        currentRemarks = parsed;
                    } else {
                        currentRemarks = [{ 
                            content: transaction.remarks, 
                            date: transaction.created_at, 
                            user: 'system' 
                        }];
                    }
                } catch (e) {
                    currentRemarks = [{ 
                        content: transaction.remarks, 
                        date: transaction.created_at, 
                        user: 'system' 
                    }];
                }
            }
        }

        const reasonText = transferData.reason ? ` (Reason: ${transferData.reason})` : '';
        const newRemark = {
          content: `Transferred to ${transferData.department} by ${transferData.transferred_by}${reasonText}`,
          date: transferData.transferred_date,
          user: user?.userid || 'admin',
          user_name: `${user?.firstname || 'Admin'} ${user?.lastname || ''}`.trim()
        };

        const { error } = await supabase
          .from('elogbook_transactions')
          .update({
            department: transferData.department,
            status: 'pending_receive',
            remarks: [...currentRemarks, newRemark],
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (error) throw error;
      }

      setSnackbar({ open: true, message: 'Documents transferred successfully', severity: 'success' });
      setOpenTransfer(false);
      setSelectedIds([]);
      setTransferData(prev => ({ ...prev, department: '', reason: '' }));
      fetchData();
    } catch (error) {
      console.error('Error transferring documents:', error);
      setSnackbar({ open: true, message: 'Error transferring documents', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReceive = async () => {
    setActionLoading(true);
    try {
      const user = authService.getUserSession();
      for (const id of selectedIds) {
        const transaction = data.find(t => t.id === id);
        if (!transaction) continue;

        let currentRemarks = [];
        if (transaction.remarks) {
            if (Array.isArray(transaction.remarks)) {
                currentRemarks = [...transaction.remarks];
            } else if (typeof transaction.remarks === 'string') {
                try {
                    const parsed = JSON.parse(transaction.remarks);
                    if (Array.isArray(parsed)) {
                        currentRemarks = parsed;
                    } else {
                        currentRemarks = [{ 
                            content: transaction.remarks, 
                            date: transaction.created_at, 
                            user: 'system' 
                        }];
                    }
                } catch (e) {
                    currentRemarks = [{ 
                        content: transaction.remarks, 
                        date: transaction.created_at, 
                        user: 'system' 
                    }];
                }
            }
        }

        const newRemark = {
          content: `Received by ${receiveData.received_by}`,
          date: receiveData.received_date,
          user: user?.userid || 'admin',
          user_name: `${user?.firstname || 'Admin'} ${user?.lastname || ''}`.trim()
        };

        const { error } = await supabase
          .from('elogbook_transactions')
          .update({
            status: 'received', 
            remarks: [...currentRemarks, newRemark],
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (error) throw error;
      }

      setSnackbar({ open: true, message: 'Documents received successfully', severity: 'success' });
      setOpenReceive(false);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      console.error('Error receiving documents:', error);
      setSnackbar({ open: true, message: 'Error receiving documents', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReadyToClaim = async () => {
    setActionLoading(true);
    try {
      const user = authService.getUserSession();
      let updatedCount = 0;

      for (const id of selectedIds) {
        const transaction = data.find(t => t.id === id);
        if (!transaction) continue;
        
        // Only update if it has a check number
        if (!transaction.check_number) continue;

        let currentRemarks = [];
        if (transaction.remarks) {
            if (Array.isArray(transaction.remarks)) {
                currentRemarks = [...transaction.remarks];
            } else if (typeof transaction.remarks === 'string') {
                try {
                    const parsed = JSON.parse(transaction.remarks);
                    if (Array.isArray(parsed)) {
                        currentRemarks = parsed;
                    } else {
                        currentRemarks = [{ 
                            content: transaction.remarks, 
                            date: transaction.created_at, 
                            user: 'system' 
                        }];
                    }
                } catch (e) {
                    currentRemarks = [{ 
                        content: transaction.remarks, 
                        date: transaction.created_at, 
                        user: 'system' 
                    }];
                }
            }
        }

        const newRemark = {
          content: `Marked as Ready to Claim`,
          date: new Date().toISOString(),
          user: user?.userid || 'admin',
          user_name: `${user?.firstname || 'Admin'} ${user?.lastname || ''}`.trim()
        };

        const { error } = await supabase
          .from('elogbook_transactions')
          .update({
            status: 'ready_to_claim', 
            remarks: [...currentRemarks, newRemark],
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (error) throw error;
        updatedCount++;
      }

      if (updatedCount > 0) {
        setSnackbar({ open: true, message: `${updatedCount} checks marked as Ready to Claim`, severity: 'success' });
        setSelectedIds([]);
        fetchData();
      } else {
        setSnackbar({ open: true, message: 'No valid checks selected', severity: 'warning' });
      }
      
    } catch (error) {
      console.error('Error updating documents:', error);
      setSnackbar({ open: true, message: 'Error updating documents', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDeleteClick = (row) => {
    setRowToDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRowToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    
    setLoading(true);
    try {
      let query = supabase.from('elogbook_transactions').delete();
      
      if (rowToDelete.isGroup && rowToDelete.linkedTransactions) {
          const ids = rowToDelete.linkedTransactions.map(t => t.id);
          query = query.in('id', ids);
      } else {
          query = query.eq('id', rowToDelete.id);
      }

      const { error } = await query;

      if (error) throw error;

      setSnackbar({
        open: true,
        message: 'Transaction deleted successfully',
        severity: 'success'
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error deleting transaction',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      handleCloseDeleteDialog();
    }
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      const user = authService.getUserSession();
      if (!user) throw new Error('User not found. Please log in again.');

      if (dialogType === 'ADD_CAFOA') {
        // Validation
        if (!formData.amount || !formData.payee_name) {
          throw new Error('Amount and Payee Name are required.');
        }

        // Duplicate check
        if (formData.cafoa_number) {
          const { data: existing, error: checkError } = await supabase
            .from('elogbook_transactions')
            .select('id')
            .eq('cafoa_number', formData.cafoa_number)
            .single();

          if (checkError && checkError.code !== 'PGRST116') throw checkError;
          if (existing) throw new Error('CAFOA Number already exists.');
        }

        const { error } = await supabase
          .from('elogbook_transactions')
          .insert([{
            cafoa_number: formData.cafoa_number || null,
            cafoa_amount: parseFloat(formData.amount),
            payee_name: formData.payee_name,
            cafoa_created_date: new Date().toISOString(),
            cafoa_created_by: user.userid,
            department: user.department,
            status: 'pending',
            remarks: [{
                content: `Transaction created`,
                date: new Date().toISOString(),
                user: user.userid,
                user_name: `${user.firstname} ${user.lastname}`
            }],
            updated_at: new Date().toISOString()
          }]);
        if (error) throw error;

      } else if (dialogType === 'ADD_VOUCHER') {
        // Allow blank DV Number
        // if (!formData.dv_number) throw new Error('DV Number is required.');

        // Check for duplicate DV Number if provided
        if (formData.dv_number && formData.dv_number.trim() !== '') {
            const { data: existingVouchers, error: searchError } = await supabase
                .from('elogbook_transactions')
                .select('id')
                .eq('dv_number', formData.dv_number);
            
            if (searchError) throw searchError;
            
            if (existingVouchers && existingVouchers.length > 0) {
                 throw new Error(`DV Number ${formData.dv_number} already exists.`);
            }
        }

        const targetIds = isBulkAction ? selectedIds : [selectedRow.id];
        
        for (const id of targetIds) {
            const row = data.find(r => r.id === id);
            if (!row) continue;

            let currentRemarks = [];
            if (row.remarks) {
                if (Array.isArray(row.remarks)) {
                    currentRemarks = [...row.remarks];
                } else if (typeof row.remarks === 'string') {
                    try {
                        const parsed = JSON.parse(row.remarks);
                        if (Array.isArray(parsed)) currentRemarks = parsed;
                        else currentRemarks = [{ content: row.remarks, date: row.created_at, user: 'system' }];
                    } catch (e) {
                        currentRemarks = [{ content: row.remarks, date: row.created_at, user: 'system' }];
                    }
                }
            }

            currentRemarks.push({
                content: `Voucher ${formData.dv_number} added${isBulkAction ? ' (Bulk)' : ''}`,
                date: new Date().toISOString(),
                user: user.userid,
                user_name: `${user.firstname} ${user.lastname}`
            });
            
            // In bulk mode, we use the row's CAFOA amount as the voucher amount for that line item
            // In single mode, we use the form data amount (which user might have edited)
            const voucherAmount = isBulkAction ? (parseFloat(row.cafoa_amount) || 0) : parseFloat(formData.voucher_amount);

            const { error } = await supabase
              .from('elogbook_transactions')
              .update({
                dv_number: formData.dv_number || null,
                voucher_type: formData.voucher_type,
                voucher_payee: formData.voucher_payee,
                voucher_amount: voucherAmount,
                voucher_particulars: formData.voucher_particulars,
                voucher_created_date: new Date().toISOString(),
                voucher_created_by: user.userid,
                status: 'obligated',
                remarks: currentRemarks,
                updated_at: new Date().toISOString()
              })
              .eq('id', id);
            
            if (error) throw error;
        }

      } else if (dialogType === 'ADD_CHECK') {
        if (!formData.check_number) throw new Error('Check Number is required.');

        let currentRemarks = [];
        if (selectedRow.remarks) {
            if (Array.isArray(selectedRow.remarks)) {
                currentRemarks = [...selectedRow.remarks];
            } else if (typeof selectedRow.remarks === 'string') {
                try {
                    const parsed = JSON.parse(selectedRow.remarks);
                    if (Array.isArray(parsed)) currentRemarks = parsed;
                    else currentRemarks = [{ content: selectedRow.remarks, date: selectedRow.created_at, user: 'system' }];
                } catch (e) {
                    currentRemarks = [{ content: selectedRow.remarks, date: selectedRow.created_at, user: 'system' }];
                }
            }
        }

        currentRemarks.push({
            content: `Check ${formData.check_number} added`,
            date: new Date().toISOString(),
            user: user.userid,
            user_name: `${user.firstname} ${user.lastname}`
        });

        let query = supabase.from('elogbook_transactions').update({
            check_number: formData.check_number,
            check_date: formData.check_date,
            check_amount: parseFloat(formData.check_amount),
            check_remarks: formData.check_remarks,
            check_created_date: new Date().toISOString(),
            check_created_by: user.userid,
            status: 'check_issued',
            remarks: currentRemarks,
            updated_at: new Date().toISOString()
        });

        if (selectedRow.isGroup && selectedRow.linkedTransactions) {
             const ids = selectedRow.linkedTransactions.map(t => t.id);
             query = query.in('id', ids);
        } else {
             query = query.eq('id', selectedRow.id);
        }

        const { error } = await query;
        if (error) throw error;

      } else if (dialogType === 'ADD_CANCELLED_CHECK') {
        if (!formData.check_number) throw new Error('Check Number is required.');
        // Payee Name is optional now

        // Insert new record with status 'cancelled'
        const { error } = await supabase
          .from('elogbook_transactions')
          .insert([{
            check_number: formData.check_number,
            check_date: formData.check_date || null,
            check_amount: parseFloat(formData.check_amount || 0),
            payee_name: formData.payee_name || '', // Store as Payee Name since it's a standalone check
            department: user.department,
            status: 'cancelled',
            check_created_date: new Date().toISOString(),
            check_created_by: user.userid,
            voucher_type: formData.voucher_type, // Include voucher_type
            remarks: [{
                content: `Cancelled Check ${formData.check_number} added`,
                date: new Date().toISOString(),
                user: user.userid,
                user_name: `${user.firstname} ${user.lastname}`,
                reason: formData.remarks || null
            }],
            updated_at: new Date().toISOString()
          }]);
        
        if (error) throw error;

      } else if (dialogType === 'EDIT_DETAILS') {
        // Check for duplicate DV Number if it's being changed and is not empty
        if (formData.dv_number && formData.dv_number.trim() !== '' && formData.dv_number !== selectedRow.dv_number) {
             const { data: existingVouchers, error: searchError } = await supabase
                .from('elogbook_transactions')
                .select('id')
                .eq('dv_number', formData.dv_number)
                .neq('id', selectedRow.id); // Exclude self
            
            if (searchError) throw searchError;
            if (existingVouchers && existingVouchers.length > 0) {
                 throw new Error(`DV Number ${formData.dv_number} already exists.`);
            }
        }

        // Calculate changes
        const changes = [];
        
        // Helper for safe comparison
        const compare = (field, newVal, oldVal) => {
            const n = newVal === undefined || newVal === null ? '' : String(newVal);
            const o = oldVal === undefined || oldVal === null ? '' : String(oldVal);
            if (n !== o) {
                changes.push({ field, new: n, old: o });
            }
        };

        // CAFOA
        compare('CAFOA Number', formData.cafoa_number, selectedRow.cafoa_number);
        compare('Payee Name', formData.payee_name, selectedRow.payee_name);
        if (parseFloat(formData.cafoa_amount || 0) !== parseFloat(selectedRow.cafoa_amount || 0)) {
            changes.push({ field: 'CAFOA Amount', new: formData.cafoa_amount, old: selectedRow.cafoa_amount });
        }

        // Voucher
        compare('DV Number', formData.dv_number, selectedRow.dv_number);
        compare('Voucher Type', formData.voucher_type, selectedRow.voucher_type || 'general');
        compare('Voucher Payee', formData.voucher_payee, selectedRow.voucher_payee);
        if (parseFloat(formData.voucher_amount || 0) !== parseFloat(selectedRow.voucher_amount || 0)) {
            changes.push({ field: 'Voucher Amount', new: formData.voucher_amount, old: selectedRow.voucher_amount });
        }
        compare('Particulars', formData.voucher_particulars, selectedRow.voucher_particulars);

        // Check
        compare('Check Number', formData.check_number, selectedRow.check_number);
        
        const newCheckDate = formData.check_date || '';
        const oldCheckDate = selectedRow.check_date ? selectedRow.check_date.split('T')[0] : '';
        if (newCheckDate !== oldCheckDate) {
             changes.push({ field: 'Check Date', new: newCheckDate, old: oldCheckDate });
        }

        if (parseFloat(formData.check_amount || 0) !== parseFloat(selectedRow.check_amount || 0)) {
            changes.push({ field: 'Check Amount', new: formData.check_amount, old: selectedRow.check_amount });
        }
        compare('Check Remarks', formData.check_remarks, selectedRow.check_remarks);

        // Prepare remarks
        let currentRemarks = [];
        if (selectedRow.remarks) {
            if (Array.isArray(selectedRow.remarks)) {
                currentRemarks = [...selectedRow.remarks];
            } else if (typeof selectedRow.remarks === 'string') {
                try {
                    const parsed = JSON.parse(selectedRow.remarks);
                    if (Array.isArray(parsed)) {
                        currentRemarks = parsed;
                    } else {
                        currentRemarks = [{ 
                            content: selectedRow.remarks, 
                            date: selectedRow.created_at, 
                            user: 'system' 
                        }];
                    }
                } catch (e) {
                    currentRemarks = [{ 
                        content: selectedRow.remarks, 
                        date: selectedRow.created_at, 
                        user: 'system' 
                    }];
                }
            }
        }

        if (changes.length > 0) {
            currentRemarks.push({
                content: `Updated transaction details`,
                changes: changes,
                date: new Date().toISOString(),
                user: user.userid,
                user_name: `${user.firstname} ${user.lastname}`
            });
        }

        const { error } = await supabase
          .from('elogbook_transactions')
          .update({
            // CAFOA
            cafoa_number: formData.cafoa_number || null,
            payee_name: formData.payee_name,
            cafoa_amount: parseFloat(formData.cafoa_amount),
            // Voucher
            dv_number: formData.dv_number || null,
            voucher_type: formData.voucher_type,
            voucher_payee: formData.voucher_payee,
            voucher_amount: parseFloat(formData.voucher_amount || 0),
            voucher_particulars: formData.voucher_particulars,
            // Check
            check_number: formData.check_number || null,
            check_date: formData.check_date || null,
            check_amount: parseFloat(formData.check_amount || 0),
            check_remarks: formData.check_remarks,
            remarks: currentRemarks,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedRow.id);
        if (error) throw error;
      } else if (dialogType === 'UPDATE_STATUS') {
        let currentRemarks = [];
        if (selectedRow.remarks) {
            if (Array.isArray(selectedRow.remarks)) {
                currentRemarks = [...selectedRow.remarks];
            } else if (typeof selectedRow.remarks === 'string') {
                try {
                    const parsed = JSON.parse(selectedRow.remarks);
                    if (Array.isArray(parsed)) currentRemarks = parsed;
                    else currentRemarks = [{ content: selectedRow.remarks, date: selectedRow.created_at, user: 'system' }];
                } catch (e) {
                    currentRemarks = [{ content: selectedRow.remarks, date: selectedRow.created_at, user: 'system' }];
                }
            }
        }

        currentRemarks.push({
            content: `Status updated to ${formData.status.replace(/_/g, ' ')}`,
            date: new Date().toISOString(),
            user: user.userid,
            user_name: `${user.firstname} ${user.lastname}`
        });

        const { error } = await supabase
          .from('elogbook_transactions')
          .update({
            status: formData.status,
            remarks: currentRemarks,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedRow.id);
        if (error) throw error;
      } else if (dialogType === 'CLAIM_CHECK') {
        if (!formData.claimed_by) throw new Error('Claimed By is required.');
        if (!formData.claimed_date) throw new Error('Claim Date is required.');

        let currentRemarks = [];
        if (selectedRow.remarks) {
            if (Array.isArray(selectedRow.remarks)) {
                currentRemarks = [...selectedRow.remarks];
            } else if (typeof selectedRow.remarks === 'string') {
                try {
                    const parsed = JSON.parse(selectedRow.remarks);
                    if (Array.isArray(parsed)) currentRemarks = parsed;
                    else currentRemarks = [{ content: selectedRow.remarks, date: selectedRow.created_at, user: 'system' }];
                } catch (e) {
                    currentRemarks = [{ content: selectedRow.remarks, date: selectedRow.created_at, user: 'system' }];
                }
            }
        }

        currentRemarks.push({
            content: `Check claimed by ${formData.claimed_by}`,
            date: new Date().toISOString(),
            user: user.userid,
            user_name: `${user.firstname} ${user.lastname}`
        });

        let query = supabase.from('elogbook_transactions').update({
            status: 'claimed',
            claimed_by: formData.claimed_by,
            claimed_date: formData.claimed_date,
            remarks: currentRemarks,
            updated_at: new Date().toISOString()
        });

        if (selectedRow.isGroup && selectedRow.linkedTransactions) {
             const ids = selectedRow.linkedTransactions.map(t => t.id);
             query = query.in('id', ids);
        } else {
             query = query.eq('id', selectedRow.id);
        }

        const { error } = await query;
        if (error) throw error;
      }

      setSnackbar({
        open: true,
        message: 'Transaction saved successfully!',
        severity: 'success'
      });
      setSelectedIds([]);
      setVoucherSelectionIds([]);
      handleCloseDialog();
      fetchData();
      fetchSuggestions();
    } catch (error) {
      console.error('Error saving transaction:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error saving transaction',
        severity: 'error'
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // Helpers
  const getDocumentType = (row) => {
    if (row.check_number) return 'Check';
    if (row.voucher_payee && String(row.voucher_payee).trim() !== '') return 'Voucher';
    return 'CAFOA';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Check': return <PaymentIcon fontSize="small" />;
      case 'Voucher': return <ReceiptIcon fontSize="small" />;
      case 'CAFOA': return <DescriptionIcon fontSize="small" />;
      default: return <DescriptionIcon fontSize="small" />;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLatestRemark = (remarks) => {
    if (!remarks) return '-';
    if (typeof remarks === 'string') return remarks; // Legacy support
    if (Array.isArray(remarks) && remarks.length > 0) {
      const lastRemark = remarks[remarks.length - 1];
      return lastRemark.content || '-';
    }
    return '-';
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'pending': 'warning',
      'received': 'info',
      'obligated': 'primary',
      'check_issued': 'info',
      'claimed': 'success',
      'ready_to_claim': 'primary',
      'for_advise': 'warning',
      'processed': 'info',
      'completed': 'success',
      'for_cafoa': 'info',
      'for_voucher': 'secondary',
      'cancelled': 'error'
    };
    return statusMap[status?.toLowerCase()] || 'info';
  };

  // Sorting Helpers
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getSortValue = (row, columnId) => {
    if (columnId === 'amount') {
        const docType = getDocumentType(row);
        return docType === 'Check' ? (row.check_amount || row.voucher_amount) : 
               docType === 'Voucher' ? (row.voucher_amount || row.cafoa_amount) : 
               row.cafoa_amount;
    }
    if (columnId === 'reference') {
        const docType = getDocumentType(row);
        return docType === 'Check' ? row.check_number : 
               docType === 'Voucher' ? row.dv_number : 
               row.cafoa_number;
    }
    if (columnId === 'payee') {
        const docType = getDocumentType(row);
        return (docType === 'Voucher' || docType === 'Check') 
               ? (row.voucher_payee || row.payee_name) 
               : row.payee_name;
    }
    if (columnId === 'type') return getDocumentType(row);
    if (columnId === 'status') return row.status;
    return row[columnId];
  };

  // Get unique statuses for filter dropdown
  const uniqueStatuses = [...new Set(data
    .map(item => item.status || 'Unknown')
  )].sort();

  // Filter Logic
  const filteredData = data.filter((row) => {
    const docType = getDocumentType(row);

    if (filterType !== 'all' && docType !== filterType) return false;
    if (filterStatus !== 'all' && (row.status || 'Unknown') !== filterStatus) return false;

    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    
    // Determine current numbers to search
    const cafoaNo = row.cafoa_number?.toLowerCase() || '';
    const dvNo = row.dv_number?.toLowerCase() || '';
    const checkNo = row.check_number?.toLowerCase() || '';
    const payee = (row.payee_name || row.voucher_payee || '').toLowerCase();
    const amount = (row.check_amount || row.voucher_amount || row.cafoa_amount || '').toString();
    const type = getDocumentType(row).toLowerCase();
    const status = (row.status || '').toLowerCase();

    return (
      cafoaNo.includes(searchLower) ||
      dvNo.includes(searchLower) ||
      checkNo.includes(searchLower) ||
      payee.includes(searchLower) ||
      amount.includes(searchLower) ||
      type.includes(searchLower) ||
      status.includes(searchLower)
    );
  }).sort((a, b) => {
    const aValue = getSortValue(a, orderBy);
    const bValue = getSortValue(b, orderBy);

    if (bValue < aValue) {
      return order === 'desc' ? -1 : 1;
    }
    if (bValue > aValue) {
      return order === 'desc' ? 1 : -1;
    }
    return 0;
  });

  const groupedData = React.useMemo(() => filteredData, [filteredData]);

  // Selection Handlers
  const handleSelectAll = (event) => {
    const selectableData = groupedData.filter(row => row.status !== 'ready_to_claim' && row.status !== 'claimed' && row.status !== 'cancelled');
    if (event.target.checked) {
      setSelectedIds(selectableData.map(n => n.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedIds.slice(0, selectedIndex),
        selectedIds.slice(selectedIndex + 1),
      );
    }
    setSelectedIds(newSelected);
  };

  // Pagination Logic
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = groupedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="800" sx={{ color: 'text.primary', mb: 0.5, letterSpacing: '-0.02em' }}>
            Document Tracking
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor financial document flow from CAFOA to Check
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip 
            label={`${groupedData.length} Records`} 
            color="primary" 
            variant="outlined" 
            size="small" 
            sx={{ fontWeight: 600, borderRadius: 2 }}
          />
          <TextField
            select
            size="small"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            SelectProps={{ native: true }}
            sx={{
              width: 150,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: theme.shadows[1],
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: `1px solid ${theme.palette.primary.main}` }
              }
            }}
          >
            <option value="all">All Types</option>
            <option value="CAFOA">CAFOA</option>
            <option value="Voucher">Voucher</option>
            <option value="Check">Check</option>
          </TextField>

          <TextField
            select
            size="small"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            SelectProps={{ native: true }}
            sx={{
              width: 150,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: theme.shadows[1],
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: `1px solid ${theme.palette.primary.main}` }
              }
            }}
          >
            <option value="all">All Statuses</option>
            {uniqueStatuses.map((status) => (
              <option key={status} value={status}>
                {status === 'Unknown' ? 'Unknown' : status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </TextField>

          <TextField
            size="small"
            placeholder="Search reference, payee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              width: { xs: 200, md: 300 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: theme.shadows[1],
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: `1px solid ${theme.palette.primary.main}` }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PostAddIcon />}
            onClick={() => selectedIds.length > 0 ? handleBulkAddVoucher() : handleOpenVoucherSelection()}
            sx={{ 
              height: 40, 
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: theme.shadows[4]
            }}
          >
            {selectedIds.length > 0 ? `Create Voucher (${selectedIds.length})` : 'Create Voucher'}
          </Button>
          {selectedIds.length > 0 && (
            <>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<ArrowForward />}
                onClick={() => setOpenTransfer(true)}
                sx={{ 
                  height: 40, 
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: theme.shadows[4]
                }}
              >
                Transfer ({selectedIds.length})
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<MoveToInbox />}
                onClick={() => setOpenReceive(true)}
                sx={{ 
                  height: 40, 
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: theme.shadows[4]
                }}
              >
                Receive ({selectedIds.length})
              </Button>
              <Button
                variant="contained"
                color="info"
                startIcon={<FactCheck />}
                onClick={handleReadyToClaim}
                sx={{ 
                  height: 40, 
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: theme.shadows[4]
                }}
              >
                Ready to Claim
              </Button>
            </>
          )}
          <Button
            variant="contained"
            color="error"
            startIcon={<PaymentIcon />}
            onClick={() => handleOpenDialog('ADD_CANCELLED_CHECK')}
            sx={{ 
              height: 40, 
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: theme.shadows[4]
            }}
          >
            Add Cancelled Check
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('ADD_CAFOA')}
            sx={{ 
              height: 40, 
              px: 3,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: theme.shadows[4],
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
            }}
          >
            New CAFOA
          </Button>
        </Stack>
      </Box>

      {/* Main Content Card */}
      <Card 
        elevation={0} 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 4,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        <TableContainer sx={{ flex: 1 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selectedIds.length > 0 && selectedIds.length < filteredData.length}
                    checked={filteredData.length > 0 && selectedIds.length === filteredData.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                {[
                  { id: 'type', label: 'Type' },
                  { id: 'reference', label: 'Reference No.' },
                  { id: 'payee', label: 'Payee' },
                  { id: 'amount', label: 'Amount', align: 'right' },
                  { id: 'status', label: 'Status' },
                  { id: 'actions', label: 'Actions', sortable: false }
                ].map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.align || 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      letterSpacing: '0.05em',
                      py: 2
                    }}
                  >
                    {headCell.sortable !== false ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading records...</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                    <Box sx={{ opacity: 0.5, mb: 2 }}>
                      <DescriptionIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                    </Box>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No records found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search terms or add a new transaction.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => {
                  const docType = getDocumentType(row);
                  const currentRefNo = 
                    docType === 'Check' ? row.check_number : 
                    docType === 'Voucher' ? row.dv_number : 
                    row.cafoa_number;
                  
                  // Amount priority: Check Amount -> Voucher Amount -> CAFOA Amount
                  let currentAmount = 0;
                  if (docType === 'Check') currentAmount = (row.check_amount || row.voucher_amount);
                  else if (docType === 'Voucher') {
                      currentAmount = row.voucher_amount || row.totalCafoaAmount || row.cafoa_amount;
                  } else {
                      currentAmount = row.cafoa_amount;
                  }

                  // Payee priority: Voucher/Check Payee -> CAFOA Payee
                  const currentPayee = 
                    (docType === 'Voucher' || docType === 'Check') 
                      ? (row.voucher_payee || row.payee_name) 
                      : row.payee_name;

                  const hasVoucher = docType !== 'CAFOA';
                  const hasCheck = !!row.check_number;

                  const getVoucherTypeLabel = () => {
                    const vType = row.voucher_type || 'General';
                    return vType.charAt(0).toUpperCase() + vType.slice(1);
                  };

                  return (
                    <TableRow 
                      key={row.id} 
                      hover
                      onClick={() => handleOpenDialog('VIEW_DETAILS', row)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      <TableCell padding="checkbox">
                      {row.status !== 'ready_to_claim' && row.status !== 'claimed' && row.status !== 'cancelled' && (
                        <Checkbox
                          color="primary"
                          checked={selectedIds.indexOf(row.id) !== -1}
                            onChange={(event) => {
                              event.stopPropagation();
                              handleSelectOne(event, row.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                           <Box
                            sx={{
                              width: 4,
                              height: 32,
                              borderRadius: 4,
                              bgcolor: 
                                docType === 'Check' ? 'success.main' : 
                                docType === 'Voucher' ? 'secondary.main' : 
                                'primary.main',
                              mr: 1
                            }}
                           />
                           <Box>
                             <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="subtitle2" fontWeight="600" sx={{ lineHeight: 1.2 }}>
                                   {docType}
                                </Typography>
                                {row.isGroup && (
                                    <Chip 
                                        label={row.linkedTransactions.length} 
                                        size="small" 
                                        color="default" 
                                        variant="filled"
                                        sx={{ height: 16, fontSize: '0.65rem', minWidth: 20, px: 0.5 }} 
                                    />
                                )}
                             </Stack>
                             {docType !== 'CAFOA' && (
                               <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                 {getVoucherTypeLabel()}
                               </Typography>
                             )}
                           </Box>
                        </Stack>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>
                          {currentRefNo || '-'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Tooltip title={currentPayee || ''}>
                          <Typography noWrap variant="body2" color="text.primary">
                            {currentPayee || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {formatCurrency(currentAmount)}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={(row.status || 'Unknown').replace(/_/g, ' ')} 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!row.status || ['pending', 'for_cafoa', 'for_voucher', 'for_advise', 'obligated', 'received', 'processed', 'check_issued'].includes(row.status)) {
                              handleOpenDialog('UPDATE_STATUS', row);
                            } else if (row.status === 'ready_to_claim') {
                              handleOpenDialog('CLAIM_CHECK', row);
                            }
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            textTransform: 'capitalize',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 24,
                            bgcolor: alpha(theme.palette[getStatusColor(row.status)].main, 0.1),
                            color: theme.palette[getStatusColor(row.status)].main,
                            border: `1px solid ${alpha(theme.palette[getStatusColor(row.status)].main, 0.2)}`,
                            '&:hover': {
                              bgcolor: alpha(theme.palette[getStatusColor(row.status)].main, 0.2),
                            }
                          }}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {!hasVoucher && (
                            <Tooltip title="Add Voucher">
                              <IconButton 
                                size="small" 
                                color="secondary"
                                onClick={(e) => { e.stopPropagation(); handleOpenDialog('ADD_VOUCHER', row); }}
                                sx={{ 
                                  bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                                  '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.2) } 
                                }}
                              >
                                <PostAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasVoucher && !hasCheck && (
                            <Tooltip title="Add Check">
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={(e) => { e.stopPropagation(); handleOpenDialog('ADD_CHECK', row); }}
                                sx={{ 
                                  bgcolor: alpha(theme.palette.success.main, 0.1), 
                                  '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) } 
                                }}
                              >
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => { e.stopPropagation(); handleOpenDialog('EDIT_DETAILS', row); }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => { e.stopPropagation(); handleDeleteClick(row); }}
                              sx={{ 
                                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } 
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={groupedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </Card>

      {/* Transfer Dialog */}
      <Dialog open={openTransfer} onClose={() => setOpenTransfer(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Documents</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Transfer {selectedIds.length} selected documents to another department.
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={departments}
              value={transferData.department}
              onChange={(e, newValue) => setTransferData({ ...transferData, department: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Select Department" fullWidth required />
              )}
            />
            <TextField
              label="Transferred By"
              fullWidth
              value={transferData.transferred_by}
              onChange={(e) => setTransferData({ ...transferData, transferred_by: e.target.value })}
            />
            <TextField
              label="Date Transferred"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={transferData.transferred_date}
              onChange={(e) => setTransferData({ ...transferData, transferred_date: e.target.value })}
            />
            <TextField
              label="Reason (Optional)"
              fullWidth
              multiline
              rows={2}
              value={transferData.reason}
              onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })}
              placeholder="Add a reason for this transfer..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTransfer(false)}>Cancel</Button>
          <Button onClick={handleTransfer} variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={openReceive} onClose={() => setOpenReceive(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Receive Documents</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Mark {selectedIds.length} documents as received.
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Received By"
              fullWidth
              value={receiveData.received_by}
              onChange={(e) => setReceiveData({ ...receiveData, received_by: e.target.value })}
            />
            <TextField
              label="Date Received"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={receiveData.received_date}
              onChange={(e) => setReceiveData({ ...receiveData, received_date: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReceive(false)}>Cancel</Button>
          <Button onClick={handleReceive} variant="contained" color="success" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Receive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dynamic Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: theme.shadows[10] }
        }}
      >
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2, fontWeight: 700 }}>
          {dialogType === 'ADD_CAFOA' && 'Add New CAFOA'}
          {dialogType === 'ADD_VOUCHER' && 'Add Voucher Details'}
          {dialogType === 'SELECT_VOUCHER_CAFOAS' && 'Select CAFOAs for Voucher'}
          {dialogType === 'ADD_CHECK' && 'Add Check Details'}
          {dialogType === 'ADD_CANCELLED_CHECK' && 'Add Cancelled Check'}
          {dialogType === 'EDIT_DETAILS' && 'Edit Transaction Details'}
          {dialogType === 'VIEW_DETAILS' && 'Transaction Details'}
          {dialogType === 'UPDATE_STATUS' && 'Update Status'}
          {dialogType === 'CLAIM_CHECK' && 'Claim Check'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Select CAFOAs for Voucher */}
            {dialogType === 'SELECT_VOUCHER_CAFOAS' && (
              <TableContainer sx={{ maxHeight: 400, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                        <Checkbox
                            indeterminate={
                                voucherSelectionIds.length > 0 &&
                                voucherSelectionIds.length < data.filter(r => !r.dv_number && r.status !== 'ready_to_claim' && r.status !== 'claimed').length
                            }
                            checked={
                                data.filter(r => !r.dv_number && r.status !== 'ready_to_claim' && r.status !== 'claimed').length > 0 &&
                                voucherSelectionIds.length === data.filter(r => !r.dv_number && r.status !== 'ready_to_claim' && r.status !== 'claimed').length
                            }
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setVoucherSelectionIds(data.filter(r => !r.dv_number && r.status !== 'ready_to_claim' && r.status !== 'claimed').map(r => r.id));
                                } else {
                                    setVoucherSelectionIds([]);
                                }
                            }}
                        />
                      </TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>CAFOA No.</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Payee</TableCell>
                      <TableCell align="right" sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.filter(r => !r.dv_number && r.status !== 'ready_to_claim' && r.status !== 'claimed').map((row) => (
                      <TableRow 
                        key={row.id} 
                        hover 
                        onClick={() => {
                            const selectedIndex = voucherSelectionIds.indexOf(row.id);
                            let newSelected = [];
                            if (selectedIndex === -1) {
                                newSelected = newSelected.concat(voucherSelectionIds, row.id);
                            } else if (selectedIndex === 0) {
                                newSelected = newSelected.concat(voucherSelectionIds.slice(1));
                            } else if (selectedIndex === voucherSelectionIds.length - 1) {
                                newSelected = newSelected.concat(voucherSelectionIds.slice(0, -1));
                            } else if (selectedIndex > 0) {
                                newSelected = newSelected.concat(
                                    voucherSelectionIds.slice(0, selectedIndex),
                                    voucherSelectionIds.slice(selectedIndex + 1),
                                );
                            }
                            setVoucherSelectionIds(newSelected);
                        }}
                        role="checkbox"
                        aria-checked={voucherSelectionIds.indexOf(row.id) !== -1}
                        selected={voucherSelectionIds.indexOf(row.id) !== -1}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={voucherSelectionIds.indexOf(row.id) !== -1} />
                        </TableCell>
                        <TableCell>{row.cafoa_number}</TableCell>
                        <TableCell>{row.payee_name}</TableCell>
                        <TableCell align="right">{formatCurrency(row.cafoa_amount)}</TableCell>
                      </TableRow>
                    ))}
                    {data.filter(r => !r.dv_number && r.status !== 'ready_to_claim' && r.status !== 'claimed').length === 0 && (
                        <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                <Typography color="text.secondary">No available CAFOAs to voucher.</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* View Details Mode */}
            {dialogType === 'VIEW_DETAILS' && selectedRow && (
              <Stack spacing={3}>
                {/* CAFOA Info */}
                <Box>
                  <Divider textAlign="left" sx={{ mb: 2 }}>
                    <Chip label="CAFOA Details" size="small" color="primary" variant="outlined" />
                  </Divider>
                  {selectedRow.isGroup ? (
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, mb: 2 }}>
                          <Table size="small" stickyHeader>
                              <TableHead>
                                  <TableRow>
                                      <TableCell sx={{ fontWeight: 600 }}>Ref No.</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Payee</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                  </TableRow>
                              </TableHead>
                              <TableBody>
                                  {selectedRow.linkedTransactions.map((t) => (
                                      <TableRow key={t.id} hover>
                                          <TableCell sx={{ fontFamily: 'monospace' }}>{t.cafoa_number || '-'}</TableCell>
                                          <TableCell>{t.payee_name}</TableCell>
                                          <TableCell align="right">{formatCurrency(t.cafoa_amount)}</TableCell>
                                          <TableCell>{formatDate(t.cafoa_created_date)}</TableCell>
                                      </TableRow>
                                  ))}
                              </TableBody>
                          </Table>
                      </TableContainer>
                  ) : (
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">CAFOA Number</Typography>
                          <Typography variant="body1" fontWeight="600">{selectedRow.cafoa_number || '-'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Amount</Typography>
                          <Typography variant="body1" fontWeight="600">{formatCurrency(selectedRow.cafoa_amount)}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Payee</Typography>
                          <Typography variant="body1" fontWeight="600">{selectedRow.payee_name || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                           <Typography variant="caption" color="text.secondary">Date Created</Typography>
                           <Typography variant="body2">{formatDate(selectedRow.cafoa_created_date)}</Typography>
                        </Grid>
                      </Grid>
                  )}
                </Box>

                {/* Voucher Info */}
                <Box>
                  <Divider textAlign="left" sx={{ mb: 2 }}>
                    <Chip label="Voucher Details" size="small" color="secondary" variant="outlined" />
                  </Divider>
                  {selectedRow.dv_number ? (
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">DV Number</Typography>
                        <Typography variant="body1" fontWeight="600">{selectedRow.dv_number}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Voucher Type</Typography>
                        <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{selectedRow.voucher_type || 'General'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Payee</Typography>
                        <Typography variant="body1" fontWeight="600">{selectedRow.voucher_payee || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Amount</Typography>
                        <Typography variant="body1" fontWeight="600">{formatCurrency(selectedRow.voucher_amount)}</Typography>
                      </Grid>
                       <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Particulars</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedRow.voucher_particulars || '-'}</Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic" align="center">
                      No voucher details available
                    </Typography>
                  )}
                </Box>

                {/* Check Info */}
                <Box>
                  <Divider textAlign="left" sx={{ mb: 2 }}>
                    <Chip label="Check Details" size="small" color="success" variant="outlined" />
                  </Divider>
                  {selectedRow.check_number ? (
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Check Number</Typography>
                        <Typography variant="body1" fontWeight="600">{selectedRow.check_number}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <Typography variant="body1">{formatDate(selectedRow.check_date)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Amount</Typography>
                        <Typography variant="body1" fontWeight="600">{formatCurrency(selectedRow.check_amount)}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Remarks</Typography>
                        <Typography variant="body2">{selectedRow.check_remarks || '-'}</Typography>
                      </Grid>
                      {/* Check Reason from Remarks if it's a cancelled check */}
                      {selectedRow.status === 'cancelled' && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Reason</Typography>
                            <Typography variant="body2">
                                {(() => {
                                    if (selectedRow.remarks) {
                                        let remarks = selectedRow.remarks;
                                        if (typeof remarks === 'string') {
                                            try {
                                                const parsed = JSON.parse(remarks);
                                                if (Array.isArray(parsed)) remarks = parsed;
                                            } catch (e) {}
                                        }
                                        if (Array.isArray(remarks)) {
                                            const reasonRemark = remarks.find(r => r.content && r.content.includes('Cancelled Check') && r.reason);
                                            return reasonRemark ? reasonRemark.reason : '-';
                                        }
                                    }
                                    return '-';
                                })()}
                            </Typography>
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic" align="center">
                      No check details available
                    </Typography>
                  )}
                </Box>

                {/* Claim Info */}
                {(selectedRow.status === 'claimed' || selectedRow.claimed_by) && (
                  <Box>
                    <Divider textAlign="left" sx={{ mb: 2 }}>
                      <Chip label="Claim Details" size="small" color="info" variant="outlined" />
                    </Divider>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Claimed By</Typography>
                        <Typography variant="body1" fontWeight="600">{selectedRow.claimed_by}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Date Claimed</Typography>
                        <Typography variant="body1">{formatDate(selectedRow.claimed_date)}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Transaction History */}
                <Box>
                  <Divider textAlign="left" sx={{ mb: historyExpanded ? 3 : 0 }}>
                    <Chip 
                      icon={<HistoryIcon sx={{ fontSize: '1rem !important' }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            Transaction History
                            {historyExpanded ? <ExpandLess sx={{ fontSize: '1rem' }} /> : <ExpandMore sx={{ fontSize: '1rem' }} />}
                        </Box>
                      }
                      onClick={() => setHistoryExpanded(!historyExpanded)}
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                      sx={{ 
                          fontWeight: 600, 
                          border: '1px solid', 
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': { 
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              borderColor: 'primary.main'
                          }
                      }}
                    />
                  </Divider>
                  <Collapse in={historyExpanded}>
                  {(() => {
                    let remarks = selectedRow.remarks;
                    
                    // Parse JSON string if necessary
                    if (typeof remarks === 'string') {
                        try {
                            const parsed = JSON.parse(remarks);
                            if (Array.isArray(parsed)) remarks = parsed;
                        } catch (e) {
                            // Keep as string
                        }
                    }

                    if (!remarks || (Array.isArray(remarks) && remarks.length === 0)) {
                        return (
                            <Box sx={{ py: 3, textAlign: 'center', opacity: 0.6 }}>
                                <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">No history recorded</Typography>
                            </Box>
                        );
                    }
                    
                    // Legacy string support
                    if (typeof remarks === 'string') {
                        return (
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                <Typography variant="body2">{remarks}</Typography>
                            </Paper>
                        );
                    }

                    // Array support
                    const sortedRemarks = [...remarks].reverse();
                    
                    return (
                        <Stack spacing={0} sx={{ position: 'relative', px: 1 }}>
                            {sortedRemarks.map((remark, index) => (
                                <HistoryItem 
                                    key={index} 
                                    remark={remark} 
                                    index={index} 
                                    isLast={index === sortedRemarks.length - 1}
                                    formatDate={formatDate}
                                />
                            ))}
                        </Stack>
                    );
                  })()}
                  </Collapse>
                </Box>
              </Stack>
            )}

            {/* CAFOA Fields */}
            {(dialogType === 'ADD_CAFOA' || dialogType === 'EDIT_DETAILS') && (
              <>
                {dialogType === 'EDIT_DETAILS' && (
                  <Divider textAlign="left">
                    <Chip label="CAFOA Details" size="small" color="primary" variant="outlined" />
                  </Divider>
                )}
                {dialogType === 'EDIT_DETAILS' && selectedRow?.isGroup && (
                    <Alert severity="warning">
                        This is a grouped voucher. CAFOA details cannot be bulk edited.
                    </Alert>
                )}
                <TextField
                  autoFocus={dialogType === 'ADD_CAFOA'}
                  label="CAFOA Number"
                  name="cafoa_number"
                  value={formData.cafoa_number}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="Optional (Must be unique if provided)"
                  helperText="Leave blank if not yet available"
                  variant="outlined"
                  disabled={dialogType === 'EDIT_DETAILS' && selectedRow?.isGroup}
                />
                <Autocomplete
                  freeSolo
                  options={payeeSuggestions}
                  value={formData.payee_name || ''}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      payee_name: newInputValue
                    }));
                  }}
                  disabled={dialogType === 'EDIT_DETAILS' && selectedRow?.isGroup}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payee Name"
                      name="payee_name"
                      fullWidth
                      required={dialogType === 'ADD_CAFOA'}
                    />
                  )}
                />
                <TextField
                  label="Amount"
                  name={dialogType === 'EDIT_DETAILS' ? 'cafoa_amount' : 'amount'}
                  type="number"
                  value={dialogType === 'EDIT_DETAILS' ? formData.cafoa_amount : formData.amount}
                  onChange={handleInputChange}
                  fullWidth
                  required={dialogType === 'ADD_CAFOA'}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₱</InputAdornment>,
                  }}
                  disabled={dialogType === 'EDIT_DETAILS' && selectedRow?.isGroup}
                />
              </>
            )}

            {/* Voucher Fields */}
            {(dialogType === 'ADD_VOUCHER' || (dialogType === 'EDIT_DETAILS' && selectedRow?.dv_number)) && (
              <>
                {dialogType === 'ADD_VOUCHER' && isBulkAction && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Creating voucher for {selectedIds.length} selected CAFOAs.
                        <br/>
                        Total Amount: {formatCurrency(formData.voucher_amount)}
                    </Alert>
                )}
                {(dialogType === 'EDIT_DETAILS' || dialogType === 'ADD_VOUCHER') && (
                  <Divider textAlign="left">
                    <Chip label="Voucher Details" size="small" color="secondary" variant="outlined" />
                  </Divider>
                )}
                <TextField
                  label="DV Number"
                  name="dv_number"
                  value={formData.dv_number}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="Optional"
                />
                <TextField
                  label="Voucher Type"
                  name="voucher_type"
                  value={formData.voucher_type || 'general'}
                  onChange={handleInputChange}
                  select
                  SelectProps={{ native: true }}
                  fullWidth
                >
                  <option value="general">General</option>
                  <option value="trust">Trust</option>
                  <option value="sef">SEF</option>
                  <option value="calamity">Calamity</option>
                  <option value="philhealth">Philhealth</option>
                </TextField>
                <TextField
                  label="Voucher Payee"
                  name="voucher_payee"
                  value={formData.voucher_payee}
                  onChange={handleInputChange}
                  fullWidth
                />
                <TextField
                  label="Voucher Amount"
                  name="voucher_amount"
                  type="number"
                  value={formData.voucher_amount}
                  onChange={handleInputChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₱</InputAdornment>,
                  }}
                />
                <Autocomplete
                  freeSolo
                  options={particularsSuggestions}
                  value={formData.voucher_particulars || ''}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      voucher_particulars: newInputValue
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Particulars"
                      name="voucher_particulars"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  )}
                />
              </>
            )}

            {/* Cancelled Check Fields - Custom Order */}
            {dialogType === 'ADD_CANCELLED_CHECK' && (
              <>
                 <Divider textAlign="left">
                    <Chip label="Cancelled Check Details" size="small" color="error" variant="outlined" />
                 </Divider>
                 <TextField
                  inputRef={checkNumberInputRef}
                  autoFocus
                  label="Check Number"
                  name="check_number"
                  value={formData.check_number}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                <TextField
                  label="Voucher Type"
                  name="voucher_type"
                  value={formData.voucher_type || 'general'}
                  onChange={handleInputChange}
                  select
                  SelectProps={{ native: true }}
                  fullWidth
                >
                  <option value="general">General</option>
                  <option value="trust">Trust</option>
                  <option value="sef">SEF</option>
                  <option value="calamity">Calamity</option>
                  <option value="philhealth">Philhealth</option>
                </TextField>
                <Autocomplete
                  freeSolo
                  options={remarksSuggestions}
                  value={formData.remarks}
                  onChange={(event, newValue) => {
                    handleInputChange({
                      target: { name: 'remarks', value: newValue }
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Remarks / Reason"
                      name="remarks"
                      onChange={handleInputChange}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Optional"
                      onKeyDown={(e) => {
                        // Allow submitting with Enter (unless Shift+Enter is pressed for new line)
                        // Or since user asked "all input fields", let's prioritize submit
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit();
                        }
                      }}
                    />
                  )}
                />
                <TextField
                  label="Check Date"
                  name="check_date"
                  type="date"
                  value={formData.check_date}
                  onChange={handleInputChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                <TextField
                  label="Check Amount"
                  name="check_amount"
                  type="number"
                  value={formData.check_amount}
                  onChange={handleInputChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₱</InputAdornment>,
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                <TextField
                  label="Payee Name"
                  name="payee_name"
                  value={formData.payee_name}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="Optional"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
              </>
            )}

            {/* Check Fields (Standard) */}
            {(dialogType === 'ADD_CHECK' || (dialogType === 'EDIT_DETAILS' && selectedRow?.check_number)) && (
              <>
                {(dialogType === 'EDIT_DETAILS' || dialogType === 'ADD_CHECK') && (
                  <Divider textAlign="left">
                    <Chip label="Check Details" size="small" color="success" variant="outlined" />
                  </Divider>
                )}
                <TextField
                  autoFocus={dialogType === 'ADD_CHECK'}
                  label="Check Number"
                  name="check_number"
                  value={formData.check_number}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
                <TextField
                  label="Check Date"
                  name="check_date"
                  type="date"
                  value={formData.check_date}
                  onChange={handleInputChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Check Amount"
                  name="check_amount"
                  type="number"
                  value={formData.check_amount}
                  onChange={handleInputChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₱</InputAdornment>,
                  }}
                />
              </>
            )}

            {/* Update Status Field */}
            {dialogType === 'UPDATE_STATUS' && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Update status for this transaction. Current status: <strong>{(selectedRow?.status || 'Unknown').replace(/_/g, ' ')}</strong>
                </Typography>
                <TextField
                  select
                  label="New Status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  SelectProps={{ native: true }}
                  fullWidth
                >
                  <option value="pending">Pending</option>
                  <option value="for_cafoa">For CAFOA</option>
                  <option value="for_voucher">For Voucher</option>
                  <option value="for_advise">For Advise</option>
                  <option value="ready_to_claim">Ready to Claim</option>
                </TextField>
              </>
            )}

            {/* Claim Check Fields */}
            {dialogType === 'CLAIM_CHECK' && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Please enter details of the person claiming the check.
                </Typography>
                <TextField
                  label="Claimed By"
                  name="claimed_by"
                  value={formData.claimed_by}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  placeholder="Full Name"
                />
                <TextField
                  label="Claim Date"
                  name="claimed_date"
                  type="date"
                  value={formData.claimed_date}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
            
            {/* Show info if editing but sections missing */}
            {dialogType === 'EDIT_DETAILS' && !selectedRow?.dv_number && (
               <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', textAlign: 'center' }}>
                 * Voucher details not yet available. Add Voucher first to edit.
               </Typography>
            )}
            {dialogType === 'EDIT_DETAILS' && selectedRow?.dv_number && !selectedRow?.check_number && (
               <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', textAlign: 'center' }}>
                 * Check details not yet available. Add Check first to edit.
               </Typography>
            )}

          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleCloseDialog} disabled={submitLoading} sx={{ borderRadius: 2 }}>
            {dialogType === 'VIEW_DETAILS' ? 'Close' : 'Cancel'}
          </Button>
          {dialogType === 'SELECT_VOUCHER_CAFOAS' ? (
             <Button 
                onClick={() => {
                    setSelectedIds(voucherSelectionIds);
                    handleBulkAddVoucher(voucherSelectionIds);
                }} 
                variant="contained" 
                color="secondary"
                disabled={voucherSelectionIds.length === 0}
            >
                Next ({voucherSelectionIds.length})
            </Button>
          ) : dialogType !== 'VIEW_DETAILS' && (
            <Button 
              type="submit"
              variant="contained" 
              disabled={submitLoading}
              sx={{ borderRadius: 2, px: 3 }}
            >
              {submitLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Transaction'}
            </Button>
          )}
        </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: theme.shadows[10] }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ color: 'error.main', fontWeight: 700 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
            {rowToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  <strong>Ref:</strong> {rowToDelete.cafoa_number || rowToDelete.dv_number || rowToDelete.check_number || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Payee:</strong> {rowToDelete.payee_name || rowToDelete.voucher_payee || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Amount:</strong> {formatCurrency(rowToDelete.cafoa_amount || rowToDelete.voucher_amount || rowToDelete.check_amount)}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error" autoFocus sx={{ borderRadius: 2, px: 3 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>



      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%', borderRadius: 2, boxShadow: theme.shadows[3] }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const HistoryItem = ({ remark, index, isLast, formatDate }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  
  const content = remark.content || '';
  const date = remark.date ? formatDate(remark.date) : '';
  const hasChanges = remark.changes && remark.changes.length > 0;
  
  // Determine type and icon
  let type = 'update';
  let Icon = EditIcon;
  let color = 'primary';
  
  if (content.toLowerCase().includes('transferred')) {
      type = 'transfer';
      Icon = ArrowForward;
      color = 'secondary';
  } else if (content.toLowerCase().includes('received')) {
      type = 'receive';
      Icon = MoveToInbox;
      color = 'success';
  } else if (content.toLowerCase().includes('created') || content.toLowerCase().includes('added')) {
      type = 'create';
      Icon = AddIcon;
      color = 'info';
  }

  // Extract Reason if present
  const reasonMatch = content.match(/\(Reason: (.*)\)/);
  const reason = reasonMatch ? reasonMatch[1] : null;
  const mainContent = reason ? content.replace(`(Reason: ${reason})`, '').replace('()', '').trim() : content;
  
  // User info
  const userName = remark.user_name || (remark.user === 'system' ? 'System' : remark.user) || 'Unknown User';

  return (
      <Box sx={{ position: 'relative', pl: 4, pb: 3, '&:last-child': { pb: 0 } }}>
          {/* Connector Line */}
          {!isLast && (
              <Box sx={{ 
                  position: 'absolute', 
                  left: 11, 
                  top: 24, 
                  bottom: -4, 
                  width: 2, 
                  bgcolor: 'divider',
                  zIndex: 0
              }} />
          )}
          
          {/* Icon Bubble */}
          <Box sx={{ 
              position: 'absolute', 
              left: 0, 
              top: 0, 
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: `${color}.main`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              zIndex: 1,
              boxShadow: 2
          }}>
              <Icon sx={{ fontSize: 14 }} />
          </Box>
          
          {/* Content Card */}
          <Paper 
              elevation={0} 
              variant="outlined"
              sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                      borderColor: `${color}.main`,
                      boxShadow: '0 2px 8px 0 rgba(0,0,0,0.05)'
                  }
              }}
          >
              <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ lineHeight: 1.3 }}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {date}
                      </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {mainContent}
                      {userName && <span style={{ opacity: 0.7 }}> — by {userName}</span>}
                  </Typography>
                  
                  {reason && (
                      <Box sx={{ 
                          mt: 1, 
                          p: 1.5, 
                          bgcolor: alpha(theme.palette.warning.main, 0.05), 
                          borderRadius: 1,
                          border: '1px dashed',
                          borderColor: alpha(theme.palette.warning.main, 0.3)
                      }}>
                          <Typography variant="caption" fontWeight="600" color="warning.main" display="block" gutterBottom>
                              Reason:
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                              {reason}
                          </Typography>
                      </Box>
                  )}

                  {hasChanges && (
                      <Box>
                           <Button 
                              size="small" 
                              onClick={() => setExpanded(!expanded)}
                              endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                              sx={{ mt: 1, textTransform: 'none' }}
                          >
                              {expanded ? 'Hide Changes' : 'View Changes'}
                          </Button>
                          <Collapse in={expanded}>
                              <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                  <Table size="small">
                                      <TableHead>
                                          <TableRow>
                                              <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>Field</TableCell>
                                              <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>Old Value</TableCell>
                                              <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>New Value</TableCell>
                                          </TableRow>
                                      </TableHead>
                                      <TableBody>
                                          {remark.changes.map((change, i) => (
                                              <TableRow key={i}>
                                                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{change.field}</TableCell>
                                                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'error.main' }}>
                                                      {change.old || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>(empty)</span>}
                                                  </TableCell>
                                                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'success.main' }}>
                                                      {change.new || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>(empty)</span>}
                                                  </TableCell>
                                              </TableRow>
                                          ))}
                                      </TableBody>
                                  </Table>
                              </Box>
                          </Collapse>
                      </Box>
                  )}
              </Stack>
          </Paper>
      </Box>
  );
};

export default DocumentTracking;
